import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLogin, useSignup } from '../hooks/useGame';
import { requestPasswordReset } from '../services/api';
import './LoginModal.css';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type AuthMode = 'login' | 'signup' | 'forgot';

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
    const [mode, setMode] = useState<AuthMode>('login');
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Form state
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [isResetLoading, setIsResetLoading] = useState(false);


    const { mutate: login, isPending: isLoginPending } = useLogin();
    const { mutate: signup, isPending: isSignupPending } = useSignup();

    // Combined loading state
    const isLoading = isLoginPending || isSignupPending || isResetLoading;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);

        if (mode === 'forgot') {
            // Handle password reset request
            setIsResetLoading(true);
            try {
                const response = await requestPasswordReset(email);
                if (response.success) {
                    setSuccessMessage(response.message || 'Check your email for a reset link.');
                    setEmail('');
                } else {
                    setError(response.message || 'Failed to send reset email.');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to send reset email. Please try again.');
            } finally {
                setIsResetLoading(false);
            }
            return;
        }

        if (mode === 'login') {
            login(
                { username, password },
                {
                    onSuccess: () => {
                        onClose();
                        setUsername('');
                        setPassword('');
                        setEmail('');
                    },
                    onError: (err: any) => {
                        setError(err.message || 'Login failed');
                    }
                }
            );
        } else {
            signup(
                { username, email, password },
                {
                    onSuccess: () => {
                        onClose();
                        setUsername('');
                        setPassword('');
                        setEmail('');
                    },
                    onError: (err: any) => {
                        setError(err.message || 'Signup failed');
                    }
                }
            );
        }
    };

    const switchToForgot = () => {
        setMode('forgot');
        setError(null);
        setSuccessMessage(null);
    };

    const switchToLogin = () => {
        setMode('login');
        setError(null);
        setSuccessMessage(null);
    };

    const getTitle = () => {
        switch (mode) {
            case 'forgot': return 'Reset Password';
            case 'signup': return 'Create Account';
            default: return 'Welcome Back';
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="login-modal-overlay" onClick={onClose}>
                    <motion.div
                        className="login-modal"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="login-modal-header">
                            <h2 className="login-modal-title">
                                {getTitle()}
                            </h2>
                            <button className="login-modal-close" onClick={onClose}>×</button>
                        </div>

                        {mode !== 'forgot' && (
                            <nav className="auth-tabs">
                                <button
                                    className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
                                    onClick={() => { setMode('login'); setError(null); setSuccessMessage(null); }}
                                >
                                    Log In
                                    {mode === 'login' && (
                                        <motion.div
                                            className="auth-tab-underline"
                                            layoutId="auth-tab-underline"
                                            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                                        />
                                    )}
                                </button>
                                <button
                                    className={`auth-tab ${mode === 'signup' ? 'active' : ''}`}
                                    onClick={() => { setMode('signup'); setError(null); setSuccessMessage(null); }}
                                >
                                    Sign Up
                                    {mode === 'signup' && (
                                        <motion.div
                                            className="auth-tab-underline"
                                            layoutId="auth-tab-underline"
                                            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                                        />
                                    )}
                                </button>
                            </nav>
                        )}

                        {error && (
                            <div className="auth-error">
                                {error}
                            </div>
                        )}

                        {successMessage && (
                            <div className="auth-success">
                                {successMessage}
                            </div>
                        )}

                        <div className="auth-form-container">
                            <form onSubmit={handleSubmit} className="auth-form">
                                {mode === 'forgot' ? (
                                    <>
                                        <p className="auth-description">
                                            Enter your email address and we'll send you a link to reset your password.
                                        </p>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="reset-email">Email</label>
                                            <input
                                                id="reset-email"
                                                type="email"
                                                className="form-input"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                placeholder="Enter your email"
                                                required
                                                autoComplete="email"
                                            />
                                        </div>
                                        <button type="submit" className="submit-btn" disabled={isLoading}>
                                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                                        </button>
                                        <button
                                            type="button"
                                            className="back-to-login-btn"
                                            onClick={switchToLogin}
                                        >
                                            ← Back to Login
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="username">Username</label>
                                            <input
                                                id="username"
                                                type="text"
                                                className="form-input"
                                                value={username}
                                                onChange={e => setUsername(e.target.value)}
                                                placeholder={mode === 'login' ? "Enter your username" : "Choose a username"}
                                                required
                                                autoComplete="username"
                                            />
                                        </div>

                                        <div className={`form-group email-field ${mode === 'signup' ? 'visible' : ''}`}>
                                            <label className="form-label" htmlFor="email">Email</label>
                                            <input
                                                id="email"
                                                type="email"
                                                className="form-input"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                placeholder="Enter your email"
                                                required={mode === 'signup'}
                                                autoComplete="email"
                                                tabIndex={mode === 'signup' ? 0 : -1}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label" htmlFor="password">Password</label>
                                            <input
                                                id="password"
                                                type="password"
                                                className="form-input"
                                                value={password}
                                                onChange={e => setPassword(e.target.value)}
                                                placeholder={mode === 'login' ? "Enter your password" : "Choose a password"}
                                                required
                                                autoComplete={mode === 'login' ? "current-password" : "new-password"}
                                            />
                                        </div>

                                        {mode === 'login' && (
                                            <span
                                                className="forgot-password-link"
                                                onClick={switchToForgot}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={e => e.key === 'Enter' && switchToForgot()}
                                            >
                                                Forgot your password?
                                            </span>
                                        )}

                                        <button type="submit" className="submit-btn" disabled={isLoading}>
                                            {isLoading ? 'Please wait...' : (mode === 'login' ? 'Log In' : 'Sign Up')}
                                        </button>

                                        <p className={`auth-note ${mode === 'signup' ? 'visible' : ''}`}>
                                            Sign up to sync your progress across devices and track your streaks.
                                        </p>
                                    </>
                                )}
                            </form>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
